// Vario-Slot: products.articles.detail.tabs.listings.detail.{VARIO_APP_ID}
//
// Gambio-Modell (aktualisiert 2026-07-08, Slot-Tree-Picker): Die Kategorie-Zuweisung passiert
// jetzt IM Slot (loadCategoryPicker, hierarchischer Mehrfachauswahl-Tree-Picker aus
// ../lib/category-tree-picker.ts) und schreibt direkt auf `categoryRefs` des Vario-Listings —
// das ist auch schon VOR der ersten Übertragung möglich. Die automatische Erst-Registrierung
// (autoRegisterFromChannel) liest weiterhin einmalig categoryRefs[0] nach, damit eine neue
// Zeile nicht kategorielos im Lister landet; ist noch keine Kategorie gesetzt, wird trotzdem
// registriert (null → Default-Vorlage) und der Picker erlaubt die Zuweisung direkt danach.
//
// URL-Params (Vario): articleId, listingId.
// Full-Height-Slot: layout 100%-Ancestor-Kette + internes Scrolling (wie orders-page.ts).

import type { SupabaseClient } from '@supabase/supabase-js';
import { renderListingDetailPane } from './shared/listing-detail-pane.js';
import { GAMBIO_PRODUCT_TYPES } from '../lib/gambio-product-field-catalogs.js';
import { loadShippingStatusCatalog } from './settings/data.js';
import { renderCategoryTreePicker, type CategoryTreeNode } from '../lib/category-tree-picker.js';
import { infoBadge } from '../lib/info-badge.js';
import { registerOverviewSave } from '../lib/overview-save-registry.js';
import { gambioProductId, gambioProductFrontendUrl, gambioProductAdminUrl } from '../lib/gambio-links.js';
import { fetchShopCategories } from '../lib/gambio-shop-categories.js';
import { awaitJob } from '../lib/await-job.js';
import { summarizeBulkOutcome, jobStepLabel, type BulkJobOutcome } from '../lib/bulk-failure-text.js';
import { openModal } from '@vario-connect/frontend-design';
import { helpButtonHtml, wireHelpButtons } from '../lib/help-panel.js';
import { connectionPillHtml } from '../lib/connection-pill.js';
import { describeGambioError } from '../lib/gambio-error-text.js';
import { openErrorModal } from '@vario-connect/frontend-design';

interface SlotCtx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind: string };
  supabaseUrl: string;
}

interface InboxRow {
  id: string;
  gambio_category_id: string | null;
  vario_category_id: string | null;
  vario_category_name: string | null;
  status: string;
  is_variant_child: boolean | null;
}

export async function renderListingStatus(ctx: SlotCtx): Promise<void> {
  ctx.contentEl.hidden = false;

  // KERN für die Höhe: bridge.css setzt html/body/main#app auf height:100% + overflow:hidden
  // (für die Dashboard-/Vollflächen-Slots). Dadurch ist body.scrollHeight = Viewport statt
  // Inhalt → das Auto-Höhen-iframe wächst NIE über den Viewport (Felder werden abgeschnitten).
  // Wie Ottos otto-listing-status brechen wir diese Kette per <html>-Klasse + hochspezifischem
  // Override auf (siehe renderShell): height:auto + overflow:visible → scrollHeight = echter Inhalt.
  document.documentElement.classList.add('go-lister-autogrow');

  // Inline-Artikel-Detail-Slot mit AUTO-Höhe: KEINE 100%-Ancestor-Kette erzwingen.
  // main.ts hat initVarioIframeHeight('auto') gestartet → der Helper misst body.scrollHeight
  // und meldet sie an Vario. Würde der Content auf 100%/overflow:hidden gezwängt, kollabierte
  // scrollHeight und der Slot bliebe leer. Der Content fliesst daher natürlich.
  ctx.contentEl.innerHTML = renderShell();

  wireHelpButtons(ctx.contentEl);

  const params = readSlotParams();
  if (!params.articleId || !params.listingId) {
    setStatusBar(ctx.contentEl, 'error', `URL-Parameter unvollständig. articleId=${params.articleId ?? '∅'}, listingId=${params.listingId ?? '∅'}`);
    return;
  }
  const articleId = params.articleId;
  const listingId = params.listingId;

  // Sync-Gap-Guard: die Kanal-Kategorie wird nur EINMAL pro Slot-Aufruf aus Vario nachgezogen
  // (siehe refresh()), sonst könnte eine dauerhaft leere categoryRefs eine Endlosschleife erzeugen.
  let categoryResyncAttempted = false;
  // Schlüssel des zuletzt gerenderten Zustands (Status + Kategorie) - für den nicht-destruktiven
  // Fokus-Resync (siehe onFocusResync): nur bei echter Änderung neu rendern.
  let lastResyncKey: string | null = null;

  await refresh();

  // Kategorie im Vario-Verkaufskanal (Eltern-Formular) geändert? Vario sendet dazu KEIN Event an
  // den Slot. Deshalb ziehen wir bei Fokus/Sichtbarkeit die Kategorie erneut nach: refresh() liest
  // die Zeile frisch und stößt den Vario-Re-Sync nur an, wenn noch keine Kategorie gesetzt ist
  // (günstig - sonst nur ein DB-Read + Re-Render). Der „Aktualisieren"-Button erzwingt zusätzlich
  // einen unbedingten Re-Pull. focusResyncBusy verhindert überlappende Läufe.
  let focusResyncBusy = false;
  const onFocusResync = (): void => {
    if (focusResyncBusy) return;
    focusResyncBusy = true;
    // NICHT-destruktiv: erst ein leichter Read, und nur bei tatsächlicher Änderung von Status/
    // Kategorie neu rendern. Sonst nichts anfassen - ein bloßer Fokuswechsel (z. B. Datei aus dem
    // Betriebssystem in die Bild-Dropzone ziehen → window 'focus') darf den Slot NICHT neu aufbauen,
    // weil renderInLister die Dropzone zerstört und das laufende Drag&Drop abbricht.
    void (async () => {
      try {
        const { data } = await ctx.supabase
          .schema('gambio').from('listings')
          .select('status, gambio_category_id, vario_category_id')
          .eq('vario_listing_id', listingId)
          .maybeSingle();
        const d = data as { status?: string; gambio_category_id?: string | null; vario_category_id?: string | null } | null;
        const key = d ? `${d.status ?? ''}|${d.gambio_category_id ?? ''}|${d.vario_category_id ?? ''}` : '∅';
        if (key !== lastResyncKey) {
          categoryResyncAttempted = false;
          await refresh();
        }
      } finally {
        focusResyncBusy = false;
      }
    })();
  };
  window.addEventListener('focus', onFocusResync);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') onFocusResync();
  });

  // ── Shop-Key ermitteln (geteilt: autoRegisterFromChannel + Sofort-Übertragen-Button) ──────
  async function resolveShopKey(): Promise<string | null> {
    const { data } = await ctx.supabase.rpc('gambio_list_shops');
    const shops = (Array.isArray(data) ? data : []) as Array<{ shop_key: string }>;
    return shops[0]?.shop_key ?? null;
  }

  async function refresh(): Promise<void> {
    setStatusBar(ctx.contentEl, 'loading', 'Lade Gambio-Lister-Status…');
    const { data, error } = await ctx.supabase
      .schema('gambio').from('listings')
      .select('id, gambio_category_id, vario_category_id, vario_category_name, status, is_variant_child')
      .eq('vario_listing_id', listingId)
      .maybeSingle();
    if (error) {
      setStatusBar(ctx.contentEl, 'error', `Status nicht ladbar: ${error.message}`);
      return;
    }
    setStatusBar(ctx.contentEl, '', '');
    const row = data as InboxRow | null;
    // Render-Schlüssel für den nicht-destruktiven Fokus-Resync aktuell halten.
    lastResyncKey = row ? `${row.status ?? ''}|${row.gambio_category_id ?? ''}|${row.vario_category_id ?? ''}` : '∅';
    if (row) {
      // Sync-Gap-Fix: Wird die Kanal-Kategorie erst NACH der Lister-Anlage oben im Verkaufskanal
      // gewählt, blieb die Zeile ohne Kategorie. Deshalb hier einmalig aus Vario nachziehen,
      // solange die Zeile noch keine Kategorie hat. WICHTIG: auch bei status='mapped' - der
      // Upsert-Pfad (gambio_upsert_listing_from_slot) ist gegen mapped gesperrt, deshalb schreibt
      // resyncCategoryFromChannel über gambio_set_listing_category (mapped-sicher).
      // Variantenkinder erben die Kategorie vom Vater → kein eigener Nachzug.
      if (
        !categoryResyncAttempted &&
        !row.is_variant_child &&
        !row.vario_category_id &&
        !row.gambio_category_id
      ) {
        categoryResyncAttempted = true;
        const changed = await resyncCategoryFromChannel(row, false);
        if (changed) { await refresh(); return; }
      }
      await renderInLister(row);
    } else {
      await autoRegisterFromChannel();
    }
  }

  // ── Kategorie-Re-Sync (mapped-sicher) ────────────────────────────────────
  // Liest die aktuell im Vario-Verkaufskanal gewählte Kategorie (categoryRefs[0] via
  // gambio-assign-listing-category) und schreibt sie via gambio_set_listing_category - das
  // funktioniert AUCH bei status='mapped' (anders als der gegen mapped gesperrte Upsert).
  // force=true schreibt auch, wenn schon eine Kategorie gesetzt ist (deckt „Kategorie geändert").
  // Gibt true zurück, wenn eine Kategorie geschrieben wurde. Ruft NICHT selbst refresh().
  async function resyncCategoryFromChannel(row: InboxRow, force: boolean): Promise<boolean> {
    setStatusBar(ctx.contentEl, 'loading', 'Kategorie aus dem Verkaufskanal aktualisieren…');
    let assigned: { varioCategoryId?: string | null; varioCategoryName?: string | null; error?: { message: string } };
    try {
      const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-assign-listing-category`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ articleId, listingId }),
      });
      assigned = (await res.json().catch(() => ({}))) as typeof assigned;
      if (!res.ok || assigned.error) throw new Error(assigned.error?.message ?? `HTTP ${res.status}`);
    } catch (e) {
      setStatusBar(ctx.contentEl, 'error', `Kategorie nicht ladbar. ${describeGambioError((e as Error).message).text}`);
      return false;
    }
    const catId = assigned.varioCategoryId ?? null;
    const catName = assigned.varioCategoryName ?? null;
    if (!catId) {
      setStatusBar(ctx.contentEl, force ? 'success' : '', force ? 'Im Verkaufskanal ist (noch) keine Kategorie hinterlegt.' : '');
      return false;
    }
    if (!force && row.vario_category_id === catId) { setStatusBar(ctx.contentEl, '', ''); return false; }
    const { error } = await ctx.supabase.rpc('gambio_set_listing_category', {
      _id: row.id, _gambio_category_id: catId, _category_name: catName, _vario_category_id: catId,
    });
    if (error) {
      setStatusBar(ctx.contentEl, 'error', `Kategorie speichern fehlgeschlagen: ${error.message}`);
      return false;
    }
    setStatusBar(ctx.contentEl, '', '');
    return true;
  }

  // ── Kategorie-Tree-Picker (Mehrfachauswahl + Hauptkategorie) ──────────────
  // Ersetzt die frühere flache Hauptkategorie-Radioliste (nur `mapped`, nur live in Gambio
  // verknüpfte Kategorien). Der Picker schreibt direkt auf `categoryRefs` des Vario-Listings
  // (Quelle der Wahrheit) - er funktioniert daher schon VOR der ersten Übertragung. Der Worker
  // (reconcileCategories, Etappe 3) gleicht Links + Hauptkategorie danach nach Gambio ab.
  async function loadCategoryPicker(host: HTMLElement, row: InboxRow): Promise<void> {
    host.innerHTML = '<div class="v-text-muted">Kategorien laden…</div>';
    interface TreeResp {
      ok?: boolean;
      tree?: CategoryTreeNode[];
      assignedGambioIds?: number[];
      mainGambioId?: number | null;
      error?: { code?: string; message?: string };
    }
    let data: TreeResp;
    try {
      const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-category-tree`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ shopKey: (await resolveShopKey()) ?? 'default', articleId, listingId }),
      });
      data = (await res.json().catch(() => ({}))) as TreeResp;
      if (!res.ok || !data.ok) throw new Error(data.error?.message ?? `HTTP ${res.status}`);
    } catch (e) {
      // Klartext statt der rohen Gambio-Antwort: bei ungültigen Zugangsdaten stand hier die
      // komplette 401-JSON, dreifach übereinander (User-Befund 2026-08-21).
      host.innerHTML = `<div class="v-text-muted">Kategorien nicht ladbar. ${escapeHtml(describeGambioError((e as Error).message).text)}</div>`;
      return;
    }
    const tree = data.tree ?? [];
    // Leerer Baum ist KEIN Fehler - ruhiger Hinweis statt eines roten Banners.
    if (tree.length === 0) {
      host.innerHTML = `<div class="v-banner v-banner-info">
        <span class="v-icon fa-light fa-sitemap"></span>
        <div>Noch keine Kategorien im Gambio-Shop synchronisiert - unter Einstellungen den Kategorien-Sync starten.</div>
      </div>`;
      return;
    }

    const byId = new Map<number, CategoryTreeNode>(tree.map((n) => [n.gambioId, n]));
    // Aktueller (gespeicherter) Zustand - wird nach erfolgreichem Speichern aktualisiert.
    let assignedIds: number[] = [...(data.assignedGambioIds ?? [])].sort((a, b) => a - b);
    let mainId: number | null = data.mainGambioId ?? null;

    // ── Speichern (identische Edge-Fn-/RPC-Logik wie zuvor der Inline-Save-Button) ──────────────
    // Gibt true bei Erfolg zurück; setzt die Slot-Statusleiste. Wirft NICHT.
    async function saveCategories(selected: number[], main: number | null): Promise<boolean> {
      // KEIN blockierender „Lade…"-Spinner: der Aufrufer hat die Auswahl bereits optimistisch
      // übernommen und das Modal geschlossen; hier folgt nur die stille Persistenz + eine terminale
      // Erfolgs-/Fehlermeldung.
      try {
        const shopKey = (await resolveShopKey()) ?? 'default';
        const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-assign-listing-category`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            articleId, listingId, shopKey,
            gambioCategoryIds: selected,
            mainGambioCategoryId: main ?? undefined,
          }),
        });
        const out = (await res.json().catch(() => ({}))) as {
          ok?: boolean;
          mainVarioCategoryId?: string | null;
          mainVarioCategoryName?: string | null;
          unresolvedGambioIds?: number[];
          error?: { code?: string; message?: string };
        };
        if (res.status === 409 && out.error?.code === 'CATEGORY_NOT_IN_VARIO') {
          setStatusBar(ctx.contentEl, 'error', 'Kategorie(n) noch nicht im Vario-Baum - bitte erst den Kategorien-Sync starten.');
          return false;
        }
        // Erfolg = HTTP-ok ohne `error`-Feld (die Fn signalisiert Fehler über `error`, nicht über fehlendes `ok`).
        if (!res.ok || out.error) throw new Error(out.error?.message ?? `HTTP ${res.status}`);

        // Mirror aktualisieren (mapped-sicher): die VARIO-Kategorie-ID geht in BEIDE Spalten
        // (gambio_category_id UND vario_category_id halten hier den Vario-Wert - Konvention dieser
        // Codebasis). Leere Auswahl → beide auf null.
        const mainVarioId = out.mainVarioCategoryId ?? null;
        const mainVarioName = out.mainVarioCategoryName ?? null;
        const { error: mirrorErr } = await ctx.supabase.rpc('gambio_set_listing_category', {
          _id: row.id, _gambio_category_id: mainVarioId, _category_name: mainVarioName, _vario_category_id: mainVarioId,
        });
        if (mirrorErr) console.warn('[listing-status] Kategorie-Mirror-Update fehlgeschlagen:', mirrorErr.message);

        // Sofort-Reconcile anstoßen - der Worker gleicht Links + Hauptkategorie nach Gambio ab.
        // Fire-and-forget: der Reconcile-Job läuft asynchron im Worker; darauf zu warten würde die
        // Rückmeldung nur blockieren (war Teil des „unendlichen Kreises").
        void (async () => {
          try {
            const { error } = await ctx.supabase.rpc('gambio_enqueue_publish_or_update', { _shop_key: shopKey, _vario_article_ids: [articleId] });
            if (error) console.warn('[listing-status] Reconcile-Anstoß fehlgeschlagen:', error.message);
          } catch (e) {
            console.warn('[listing-status] Reconcile-Anstoß fehlgeschlagen:', (e as Error).message);
          }
        })();

        const unresolved = out.unresolvedGambioIds ?? [];
        setStatusBar(
          ctx.contentEl,
          'success',
          unresolved.length > 0
            ? `Kategorien gespeichert - ${unresolved.length} Kategorie(n) übersprungen (noch nicht im Vario-Baum).`
            : 'Kategorien gespeichert.',
        );
        return true;
      } catch (e) {
        setStatusBar(ctx.contentEl, 'error', `Kategorien speichern fehlgeschlagen: ${(e as Error).message}`);
        return false;
      }
    }

    // ── Zusammenfassung im Slot (read-only) + „Kategorien wählen"-Button ─────────────────────────
    function paintSummary(): void {
      const items = assignedIds
        .map((id) => ({ id, name: byId.get(id)?.name ?? `Kategorie ${id}`, isMain: mainId === id }))
        .sort((a, b) => a.name.localeCompare(b.name, 'de'));
      const list = items.length === 0
        ? `<div class="v-text-muted" style="padding:2px 0;">Noch keine Kategorien gewählt.</div>`
        : `<ul style="list-style:none;margin:0 0 8px;padding:0;display:flex;flex-wrap:wrap;gap:6px;">
             ${items.map((it) => `
               <li style="display:inline-flex;align-items:center;gap:5px;background:#F3F4F6;border-radius:8px;padding:3px 9px;font-size:12.5px;${it.isMain ? 'background:#EAF4DD;font-weight:600;' : ''}">
                 ${it.isMain ? '<span class="v-icon fa-solid fa-star" style="color:#E0A100;font-size:10px;"></span>' : ''}
                 <span>${escapeHtml(it.name)}</span>
               </li>`).join('')}
           </ul>`;
      host.innerHTML = `
        <h4 class="klst-detail-section" style="margin:0 0 6px;">Kategorien ${infoBadge('Ordnet den Artikel Shop-Kategorien zu. Der Stern markiert die Hauptkategorie. Gezeigt wird die Zuordnung aus VARIO; der tatsächliche Stand im Shop steht oben im Statusbanner.')}</h4>
        <div class="go-catpicker-wrap">
          ${list}
          <div class="v-row v-row-sm" style="gap:8px;align-items:center;flex-wrap:wrap;">
            <button type="button" id="go-cat-edit" class="v-button v-button--ghost">
              <span class="v-icon fa-regular fa-folder-tree"></span> Kategorien wählen
            </button>
            <button type="button" id="go-resync-category" class="v-button v-button--ghost" title="Im Vario-Verkaufskanal gewählte Kategorie neu einlesen">
              <span class="v-icon fa-regular fa-rotate-right"></span> Kategorien aktualisieren
            </button>
          </div>
        </div>`;
      host.querySelector<HTMLButtonElement>('#go-cat-edit')!.addEventListener('click', openEditModal);
      // „Kategorien aktualisieren" zieht die im Vario-Verkaufskanal (Eltern-Formular) gewählte
      // Kategorie erneut aus Vario nach (Vario meldet dem Slot keine Änderung, kein Host→App-Event).
      const resyncEl = host.querySelector<HTMLButtonElement>('#go-resync-category');
      resyncEl?.addEventListener('click', () => {
        resyncEl.setAttribute('disabled', '');
        void (async () => {
          try {
            const changed = await resyncCategoryFromChannel(row, true);
            if (changed) await refresh();
          } finally {
            resyncEl.removeAttribute('disabled');
          }
        })();
      });
    }

    // ── Modal mit dem Baum-Picker ────────────────────────────────────────────────────────────────
    function openEditModal(): void {
      let draft: { selected: number[]; mainId: number | null } = { selected: [...assignedIds], mainId };
      const isDirty = (): boolean => {
        const a = [...draft.selected].sort((x, y) => x - y);
        const b = [...assignedIds].sort((x, y) => x - y);
        if (a.length !== b.length || a.some((v, i) => v !== b[i])) return true;
        return draft.mainId !== mainId;
      };
      const modal = openModal({
        title: 'Gambio-Kategorie wählen',
        icon: 'fa-folder-tree',
        width: 640,
        body: '<div id="go-catpicker-modal-tree"></div>',
        footer: `
          <button type="button" class="v-button v-button--ghost" data-modal-cancel>Abbrechen</button>
          <button type="button" class="v-button v-button--save" id="go-catpicker-modal-save" disabled>
            <span class="v-icon fa-regular fa-floppy-disk"></span> Speichern
          </button>`,
        onMount: (rootEl) => {
          // Der Artikel-Slot wächst auf volle Inhaltshöhe (go-lister-autogrow) → das iframe ist so
          // hoch wie sein gesamter Inhalt. Ein vertikal zentriertes `position:fixed`-Modal landet
          // dadurch mittig in dieser sehr hohen Fläche (weit unten, unnötiges Scrollen). Deshalb das
          // Modal hier oben im iframe verankern statt zentrieren (nur für dieses Modal).
          rootEl.style.alignItems = 'flex-start';
          const treeHost = rootEl.querySelector<HTMLElement>('#go-catpicker-modal-tree')!;
          const saveBtn = rootEl.querySelector<HTMLButtonElement>('#go-catpicker-modal-save')!;
          renderCategoryTreePicker(treeHost, {
            tree,
            selected: draft.selected,
            mainId: draft.mainId,
            onChange: (state) => {
              draft = state;
              saveBtn.disabled = !isDirty();
            },
          });
          saveBtn.addEventListener('click', () => {
            // Instant & nicht-blockierend: Auswahl SOFORT in die Zusammenfassung übernehmen und das
            // Modal schließen. Persistenz (Edge-Fn + Mirror + Reconcile) läuft im Hintergrund - KEIN
            // Warte-Spinner und KEIN blockierendes `await refresh()` (der frühere komplette Slot-Neu-
            // aufbau war der „unendliche Kreis"; die Kategorie war da längst gespeichert). Schlägt das
            // Speichern fehl, wird die optimistische Anzeige zurückgerollt (Fehler steht dann in der
            // Statusleiste).
            const prevAssigned = [...assignedIds];
            const prevMain = mainId;
            const nextSelected = [...draft.selected];
            const nextMain = draft.mainId;
            assignedIds = [...nextSelected].sort((a, b) => a - b);
            mainId = nextMain;
            paintSummary();
            modal.close();
            void (async () => {
              const ok = await saveCategories(nextSelected, nextMain);
              if (!ok) {
                assignedIds = prevAssigned;
                mainId = prevMain;
                paintSummary();
              }
            })();
          });
        },
      });
    }

    paintSummary();
  }


  // ── Darstellung im Gambio-Shop (settings-Felder) ──────────────────────────
  // Live-Read/Write ueber die Edge Fn gambio-product-display-settings. Ein Panel mit EINEM
  // Speichern-Button (nicht optimistisch pro Feld - zu viele Felder). Best-effort: Lade-Fehler
  // degradieren zu einem Hinweistext und blockieren den Slot nicht. Nur status 'mapped', keine
  // Variantenkinder (v3-Varianten tragen kein settings-Objekt) - dasselbe Gate wie loadActiveToggle.
  async function loadDisplaySettings(host: HTMLElement): Promise<void> {
    host.innerHTML = '<div class="v-text-muted">Darstellungseinstellungen laden…</div>';
    interface DisplayState {
      ok?: boolean;
      settings?: Record<string, unknown>;
      sortOrder?: number | null;
      isFsk18?: boolean | null;
      isVpeActive?: boolean | null;
      dateAvailable?: string | null;
      shippingCosts?: number | null;
      shippingTimeId?: number | null;
      productTypeId?: number | null;
      error?: { code?: string; message?: string };
    }
    const shopKey = (await resolveShopKey()) ?? 'default';
    let state: DisplayState;
    try {
      const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-product-display-settings`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ articleId, action: 'get', shopKey }),
      });
      state = (await res.json().catch(() => ({}))) as DisplayState;
      if (res.status === 404 || state.error?.code === 'NOT_LISTED') { host.innerHTML = ''; return; }
      if (!res.ok || !state.ok) throw new Error(state.error?.message ?? `HTTP ${res.status}`);
    } catch (e) {
      host.innerHTML = `<div class="v-text-muted">Darstellungseinstellungen nicht ladbar. ${escapeHtml(describeGambioError((e as Error).message).text)}</div>`;
      return;
    }

    const s = state.settings ?? {};
    const boolv = (v: unknown): boolean => v === true;
    const strv = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
    const numv = (v: unknown): string => (v === null || v === undefined || v === '' ? '' : String(v));
    // dateAvailable ('YYYY-MM-DD HH:MM:SS'): Gambios „kein Datum"-Sentinel ist 1000-01-01 (00:00:00)
    // → als LEER anzeigen (nicht „01.01.1000"). Alles vor 1900 gilt als „nicht gesetzt".
    const rawDateAvail = strv(state.dateAvailable).slice(0, 10);
    const dateVal = rawDateAvail >= '1900-01-01' ? rawDateAvail : '';

    const toggles: Array<{ key: string; label: string; on: boolean }> = [
      { key: 'showWeight', label: 'Gewicht anzeigen', on: boolv(s['showWeight']) },
      { key: 'showQuantityInfo', label: 'Lagerbestand anzeigen', on: boolv(s['showQuantityInfo']) },
      { key: 'showPriceOffer', label: '„Woanders günstiger?"-Hinweis anzeigen', on: boolv(s['showPriceOffer']) },
      { key: 'showAddedDateTime', label: 'Einstelldatum anzeigen', on: boolv(s['showAddedDateTime']) },
      { key: 'showOnStartpage', label: 'Auf der Startseite anzeigen', on: boolv(s['showOnStartpage']) },
      { key: 'onSitemap', label: 'In die Sitemap aufnehmen', on: boolv(s['onSitemap']) },
      { key: 'isFsk18', label: 'Altersfreigabe ab 18', on: boolv(state.isFsk18) },
      // Grundpreis-Anzeige (isVpeActive). Wird bei vorhandenen Grundpreisdaten automatisch gesetzt;
      // hier als sichtbare Rückkopplung + manueller Schalter.
      { key: 'isVpeActive', label: 'VPE anzeigen (Grundpreis)', on: boolv(state.isVpeActive) },
    ];
    const prioOpts = ['0.0', '0.1', '0.2', '0.3', '0.4', '0.5', '0.6', '0.7', '0.8', '0.9', '1.0'];
    const curPrio = strv(s['sitemapPriority']) || '0.5';
    const freqOpts: Array<[string, string]> = [
      ['always', 'immer'], ['hourly', 'stündlich'], ['daily', 'täglich'], ['weekly', 'wöchentlich'],
      ['monthly', 'monatlich'], ['yearly', 'jährlich'], ['never', 'nie'],
    ];
    const curFreq = strv(s['sitemapChangeFrequency']) || 'daily';
    const priceStatusOpts: Array<[string, string]> = [
      ['0', 'Normal'], ['1', 'Preis auf Anfrage'], ['2', 'Nicht käuflich'],
    ];
    const curPriceStatus = strv(s['priceStatus']) || '0';
    const curShippingTimeId = numv(state.shippingTimeId);
    const curProductTypeId = numv(state.productTypeId);

    // Lieferstatus-Optionen aus dem lokalen Katalog (Fallback = Standardwerte, siehe data.ts).
    const shippingCatalog = await loadShippingStatusCatalog(ctx, shopKey).catch(() => []);
    const shipOpts = shippingCatalog.map((e) => ({ value: e.gambio_value, label: e.label }));
    // Aktuell gesetzter Wert nicht (mehr) im Katalog → sichtbar halten.
    if (curShippingTimeId && !shipOpts.some((o) => o.value === curShippingTimeId)) {
      shipOpts.unshift({ value: curShippingTimeId, label: `ID ${curShippingTimeId}` });
    }

    host.innerHTML = `
      <details class="go-ds" open>
        <summary>
          <span class="go-ds-sum-title">Darstellung im Gambio-Shop ${infoBadge('Sichtbarkeits- und Anzeigeoptionen des Produkts (z. B. Startseite, Sitemap, Gewichtsanzeige).')}</span>
        </summary>
        <div class="go-ds-body">
          <div class="go-ds-toggles">
            ${toggles.map((t) => `
              <button type="button" class="go-sw${t.on ? ' on' : ''}" data-ds-toggle="${t.key}" data-ds-orig="${t.on ? '1' : '0'}">
                <span class="track"></span><span>${escapeHtml(t.label)}</span>
              </button>`).join('')}
          </div>
          <div class="go-ds-grid">
            <label class="go-ds-row"><span>Preisstatus</span>
              <select data-ds-field="priceStatus" data-ds-orig="${curPriceStatus}">
                ${priceStatusOpts.map(([v, l]) => `<option value="${v}"${v === curPriceStatus ? ' selected' : ''}>${l}</option>`).join('')}
              </select></label>
            <label class="go-ds-row"><span>Lieferstatus</span>
              <select data-ds-field="shippingTimeId" data-ds-orig="${curShippingTimeId}">
                ${shipOpts.map((o) => `<option value="${o.value}"${o.value === curShippingTimeId ? ' selected' : ''}>${escapeHtml(o.label)}</option>`).join('')}
              </select></label>
            <label class="go-ds-row"><span>Produkttyp</span>
              <select data-ds-field="productTypeId" data-ds-orig="${curProductTypeId}">
                ${GAMBIO_PRODUCT_TYPES.map((o) => `<option value="${o.value}"${o.value === curProductTypeId ? ' selected' : ''}>${escapeHtml(o.label)}</option>`).join('')}
              </select></label>
            <label class="go-ds-row"><span>Sortierreihenfolge</span>
              <input type="number" step="1" data-ds-field="sortOrder" data-ds-orig="${escapeHtml(numv(state.sortOrder))}" value="${escapeHtml(numv(state.sortOrder))}"></label>
            <label class="go-ds-row"><span>Sortierung auf Startseite</span>
              <input type="number" step="1" data-ds-field="startpageSortOrder" data-ds-orig="${escapeHtml(numv(s['startpageSortOrder']))}" value="${escapeHtml(numv(s['startpageSortOrder']))}"></label>
            <label class="go-ds-row"><span>Erscheinungsdatum</span>
              <input type="date" data-ds-field="dateAvailable" data-ds-orig="${escapeHtml(dateVal)}" value="${escapeHtml(dateVal)}"></label>
            <label class="go-ds-row"><span>Versandkosten (Aufschlag)</span>
              <input type="number" step="0.01" data-ds-field="shippingCosts" data-ds-orig="${escapeHtml(numv(state.shippingCosts))}" value="${escapeHtml(numv(state.shippingCosts))}"></label>
            <label class="go-ds-row"><span>Sitemap-Priorität</span>
              <select data-ds-field="sitemapPriority" data-ds-orig="${curPrio}">
                ${prioOpts.map((v) => `<option value="${v}"${v === curPrio ? ' selected' : ''}>${v}</option>`).join('')}
              </select></label>
            <label class="go-ds-row"><span>Sitemap-Änderungsfrequenz</span>
              <select data-ds-field="sitemapChangeFrequency" data-ds-orig="${curFreq}">
                ${freqOpts.map(([v, l]) => `<option value="${v}"${v === curFreq ? ' selected' : ''}>${l}</option>`).join('')}
              </select></label>
          </div>
        </div>
      </details>`;

    // Toggle-Buttons: lokaler Zustand via 'on'-Klasse (kein Netz-Call bis Speichern).
    host.querySelectorAll<HTMLButtonElement>('button[data-ds-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => btn.classList.toggle('on'));
    });

    // Kein eigener Speichern-Button mehr - der eine „Speichern" im Übersicht-Tab-Kopf ruft diese
    // Funktion über das overview-save-registry auf. Diff-basiert: nur geänderte Felder senden.
    async function runSaveDisplay(): Promise<{ ok: boolean; msg?: string }> {
      const fields: Record<string, unknown> = {};
      // Toggles → boolean-Felder (settings + isFsk18); die Edge-Fn-Allowlist routet sie korrekt.
      // Nur GEAENDERTE Toggles senden (Diff gegen den geladenen Zustand) - sonst wuerde ein Feld,
      // das die GET-Antwort nicht lieferte (→ Default false), beim Speichern still auf false gesetzt.
      host.querySelectorAll<HTMLButtonElement>('button[data-ds-toggle]').forEach((btn) => {
        const now = btn.classList.contains('on');
        if (now !== (btn.dataset.dsOrig === '1')) fields[btn.dataset.dsToggle!] = now;
      });
      // Selects + Inputs: nur GEAENDERTE Felder senden (Diff gegen den geladenen Anzeigewert
      // data-ds-orig) - verhindert dieselbe Drift wie bei den Toggles und haelt den PUT minimal.
      host.querySelectorAll<HTMLSelectElement | HTMLInputElement>('[data-ds-field]').forEach((el) => {
        const key = el.dataset.dsField!;
        const raw = el.value;
        if (raw === (el.dataset.dsOrig ?? '')) return; // unveraendert
        if (
          key === 'priceStatus' || key === 'startpageSortOrder' || key === 'sortOrder' ||
          key === 'shippingCosts' || key === 'shippingTimeId' || key === 'productTypeId'
        ) {
          if (raw.trim() !== '') fields[key] = Number(raw);
        } else if (key === 'dateAvailable') {
          // Datum gesetzt → 'YYYY-MM-DD 00:00:00'; geleert → Gambios „kein Datum"-Sentinel
          // 1000-01-01 zurückschreiben, damit das Feld wieder wirklich leer ist.
          fields[key] = raw.trim() !== '' ? `${raw} 00:00:00` : '1000-01-01 00:00:00';
        } else {
          fields[key] = raw; // sitemapPriority, sitemapChangeFrequency
        }
      });
      // Nichts geaendert? Kein Netz-Call (die Edge Fn wuerde sonst INVALID_FIELDS liefern).
      if (Object.keys(fields).length === 0) return { ok: true };
      try {
        const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-product-display-settings`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ articleId, action: 'set', fields, shopKey: (await resolveShopKey()) ?? 'default' }),
        });
        const out = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: { message?: string } };
        if (!res.ok || !out.ok) throw new Error(out.error?.message ?? `HTTP ${res.status}`);
        return { ok: true };
      } catch (e) {
        return { ok: false, msg: `Darstellung: ${(e as Error).message}` };
      }
    }
    registerOverviewSave(host, { label: 'Darstellung', run: runSaveDisplay });
  }

  // ── Google Shopping (addonValues) ─────────────────────────────────────────
  // Live-Read/Write ueber die Edge Fn gambio-product-google-shopping. Gleiches Muster wie
  // loadDisplaySettings: EIN Speichern-Button, Diff-basiertes Speichern (nur geaenderte Felder),
  // best-effort Degradierung bei Ladefehlern. Nur status 'mapped', keine Variantenkinder.
  async function loadGoogleShopping(host: HTMLElement): Promise<void> {
    host.innerHTML = '<div class="v-text-muted">Google-Shopping-Felder laden…</div>';
    interface GoogleShoppingState {
      ok?: boolean;
      productId?: number;
      fields?: Record<string, string>;
      error?: { code?: string; message?: string };
    }
    let state: GoogleShoppingState;
    try {
      const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-product-google-shopping`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ articleId, action: 'get', shopKey: (await resolveShopKey()) ?? 'default' }),
      });
      state = (await res.json().catch(() => ({}))) as GoogleShoppingState;
      if (res.status === 404 || state.error?.code === 'NOT_LISTED') { host.innerHTML = ''; return; }
      if (!res.ok || !state.ok) throw new Error(state.error?.message ?? `HTTP ${res.status}`);
    } catch (e) {
      host.innerHTML = `<div class="v-text-muted">Google-Shopping-Felder nicht ladbar. ${escapeHtml(describeGambioError((e as Error).message).text)}</div>`;
      return;
    }

    const f = state.fields ?? {};
    const strv = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
    const brand = strv(f['brand']);
    const gender = strv(f['gender']);
    const ageGroup = strv(f['ageGroup']);
    const condition = strv(f['condition']);
    const expirationDate = strv(f['expirationDate']).slice(0, 10);
    const identifierExists = strv(f['identifierExists']);

    const genderOpts: Array<[string, string]> = [
      ['', '— nicht gesetzt —'], ['male', 'männlich'], ['female', 'weiblich'], ['unisex', 'unisex'],
    ];
    const ageGroupOpts: Array<[string, string]> = [
      ['', '— nicht gesetzt —'], ['newborn', 'Neugeboren'], ['infant', 'Baby'],
      ['toddler', 'Kleinkind'], ['kids', 'Kinder'], ['adult', 'Erwachsene'],
    ];
    const conditionOpts: Array<[string, string]> = [
      ['', '— nicht gesetzt —'], ['1', 'neu'], ['2', 'gebraucht'], ['3', 'erneuert'],
    ];
    const identifierExistsOpts: Array<[string, string]> = [
      ['', '— nicht gesetzt —'], ['true', 'ja'], ['false', 'nein'],
    ];

    host.innerHTML = `
      <details class="go-ds" open>
        <summary>
          <span class="go-ds-sum-title">Google Shopping ${infoBadge('Zusatzfelder für Google Shopping (Marke, Zustand, Geschlecht, Altersgruppe …).')}</span>
        </summary>
        <div class="go-ds-body">
          <div class="go-ds-grid">
            <label class="go-ds-row"><span>Marke</span>
              <input type="text" data-ds-field="brand" data-ds-orig="${escapeHtml(brand)}" value="${escapeHtml(brand)}"></label>
            <label class="go-ds-row"><span>Geschlecht</span>
              <select data-ds-field="gender" data-ds-orig="${escapeHtml(gender)}">
                ${genderOpts.map(([v, l]) => `<option value="${v}"${v === gender ? ' selected' : ''}>${l}</option>`).join('')}
              </select></label>
            <label class="go-ds-row"><span>Altersgruppe</span>
              <select data-ds-field="ageGroup" data-ds-orig="${escapeHtml(ageGroup)}">
                ${ageGroupOpts.map(([v, l]) => `<option value="${v}"${v === ageGroup ? ' selected' : ''}>${l}</option>`).join('')}
              </select></label>
            <label class="go-ds-row"><span>Zustand</span>
              <select data-ds-field="condition" data-ds-orig="${escapeHtml(condition)}">
                ${conditionOpts.map(([v, l]) => `<option value="${v}"${v === condition ? ' selected' : ''}>${l}</option>`).join('')}
              </select></label>
            <label class="go-ds-row"><span>Ablaufdatum</span>
              <input type="date" data-ds-field="expirationDate" data-ds-orig="${escapeHtml(expirationDate)}" value="${escapeHtml(expirationDate)}"></label>
            <label class="go-ds-row"><span>Kennzeichnung vorhanden</span>
              <select data-ds-field="identifierExists" data-ds-orig="${escapeHtml(identifierExists)}">
                ${identifierExistsOpts.map(([v, l]) => `<option value="${v}"${v === identifierExists ? ' selected' : ''}>${l}</option>`).join('')}
              </select></label>
          </div>
        </div>
      </details>`;

    // Kein eigener Speichern-Button mehr - der eine „Speichern" im Übersicht-Tab-Kopf ruft diese
    // Funktion über das overview-save-registry auf. Diff-basiert: nur geänderte Felder senden.
    async function runSaveGoogleShopping(): Promise<{ ok: boolean; msg?: string }> {
      const fields: Record<string, string> = {};
      // Diff-basiert: nur geaenderte Felder senden (wie loadDisplaySettings) - leerer String ist
      // ein gueltiger Wert (loescht das Feld in Gambios addonValues), daher explizit erlaubt.
      host.querySelectorAll<HTMLSelectElement | HTMLInputElement>('[data-ds-field]').forEach((el) => {
        const key = el.dataset.dsField!;
        const raw = el.value;
        if (raw !== (el.dataset.dsOrig ?? '')) fields[key] = raw;
      });
      if (Object.keys(fields).length === 0) return { ok: true };
      try {
        const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-product-google-shopping`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ articleId, action: 'set', fields, shopKey: (await resolveShopKey()) ?? 'default' }),
        });
        const out = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: { message?: string } };
        if (!res.ok || !out.ok) throw new Error(out.error?.message ?? `HTTP ${res.status}`);
        return { ok: true };
      } catch (e) {
        return { ok: false, msg: `Google-Shopping: ${(e as Error).message}` };
      }
    }
    registerOverviewSave(host, { label: 'Google-Shopping', run: runSaveGoogleShopping });
  }

  // ── Auto-Registrierung aus dem Verkaufskanal ─────────────────────────────
  // Liest die im Vario-Verkaufskanal bereits gewählte Kategorie (categoryRefs[0] aus dem
  // Vario-Listing) und registriert den Artikel im Gambio-Lister, ohne den Händler zu fragen.
  async function autoRegisterFromChannel(): Promise<void> {
    setStatusBar(ctx.contentEl, 'loading', 'Übernehme Kategorie aus dem Verkaufskanal…');

    // Schritt 1: Artikel-Basics + Listing-Kategorie aus Vario lesen.
    type AssignResult = {
      articleName?: string | null;
      ean?: string | null;
      description?: string | null;
      isVariantParent?: boolean;
      isVariantChild?: boolean;
      parentArticleId?: string | null;
      varioCategoryId?: string | null;
      varioCategoryName?: string | null;
      error?: { code: string; message: string };
    };
    let assigned: AssignResult;
    try {
      const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-assign-listing-category`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ articleId, listingId }),
      });
      assigned = (await res.json().catch(() => ({}))) as AssignResult;
      if (!res.ok || assigned.error) {
        const msg = assigned.error?.message ?? `HTTP ${res.status}`;
        renderErrorBanner(`Artikel-Daten nicht ladbar: ${msg}`);
        return;
      }
    } catch (e) {
      renderErrorBanner(`Artikel-Daten nicht ladbar: ${(e as Error).message}`);
      return;
    }

    // Schritt 2: Storefront ermitteln.
    const storefront = await resolveShopKey();
    if (!storefront) {
      renderNoBannerError('Kein aktiver Gambio-Shop verbunden. Bitte zuerst einen Shop in den Einstellungen verbinden.');
      return;
    }

    // Schritt 3: Lister-Zeile anlegen.
    // Hilfsfunktion: Standalone-/Vater-Zeile via Upsert anlegen (Kategorie kann null sein).
    async function createStandaloneListing(varioCatId: string | null, varioCatName: string | null): Promise<boolean> {
      const { error: upErr } = await ctx.supabase.rpc('gambio_upsert_listing_from_slot', {
        _vario_article_id: articleId,
        _vario_listing_id: listingId,
        _storefront: storefront,
        _gambio_category_id: varioCatId,
        _vario_category_id: varioCatId,
        _category_name: varioCatName,
        _vario_article_name: assigned.articleName ?? null,
        _vario_article_number: null,
        _ean: assigned.ean ?? null,
        _description: assigned.description ?? null,
        _is_variant_parent: assigned.isVariantParent ?? false,
      });
      if (upErr) {
        renderErrorBanner(`Lister-Aufnahme fehlgeschlagen: ${upErr.message}`);
        return false;
      }
      return true;
    }

    // Variantenkind mit existierendem Vater-Listing → gambio_create_variant_child: erbt die
    // Kategorie RPC-intern vom Vater (Kinder tragen in Vario keine eigene Kategorie) → KEIN
    // Kategorie-Gate, direkt aufnehmen.
    let parentListingId: string | null = null;
    if (assigned.isVariantChild && assigned.parentArticleId) {
      const { data: parentRow, error: parentErr } = await ctx.supabase
        .schema('gambio').from('listings')
        .select('id')
        .eq('vario_article_id', assigned.parentArticleId)
        .eq('is_variant_parent', true)
        .maybeSingle();
      if (parentErr) {
        console.warn('[listing-status] Vater-Listing-Lookup fehlgeschlagen:', parentErr.message);
      } else if (parentRow) {
        parentListingId = (parentRow as { id: string }).id;
      }
    }
    if (parentListingId) {
      setStatusBar(ctx.contentEl, 'loading', 'Nehme in den Gambio-Lister auf…');
      const { error: cvErr } = await ctx.supabase.rpc('gambio_create_variant_child', {
        _parent_listing_id: parentListingId,
        _child_vario_article_id: articleId,
        _child_vario_listing_id: listingId,
        _child_vario_article_name: assigned.articleName ?? null,
        _child_vario_article_number: null,
      });
      if (cvErr) {
        renderErrorBanner(`Lister-Aufnahme (Variantenkind) fehlgeschlagen: ${cvErr.message}`);
        return;
      }
      await refresh();
      return;
    }

    // Nicht-Variantenkind: Ist im Verkaufskanal-Formular bereits eine Kategorie hinterlegt
    // (categoryRefs[0]), wird sie wie bisher ohne Rückfrage übernommen.
    if (assigned.varioCategoryId) {
      setStatusBar(ctx.contentEl, 'loading', 'Nehme in den Gambio-Lister auf…');
      if (await createStandaloneListing(assigned.varioCategoryId, assigned.varioCategoryName ?? null)) {
        await refresh();
      }
      return;
    }

    // Keine Kategorie in Vario → Zuweisung ERZWINGEN: Gate rendern + Modal direkt öffnen.
    // Die Lister-Zeile entsteht erst NACH der Kategorie-Wahl (registerWithCategory). Schließt der
    // Händler das Modal, bleibt der Artikel unregistriert; der Button öffnet es erneut.
    setStatusBar(ctx.contentEl, '', '');
    const gateBody = ctx.contentEl.querySelector<HTMLElement>('#slot-body')!;
    gateBody.innerHTML = `
      <div class="v-banner v-banner-info" style="margin-bottom:10px;">
        <span class="v-icon fa-light fa-circle-info"></span>
        <div>Dieser Artikel ist noch nicht im Gambio-Lister. Bitte zuerst eine <strong>Kategorie</strong> zuweisen.</div>
      </div>
      <div class="v-row v-row-sm" style="align-items:center;gap:10px;">
        <button type="button" id="go-newcat-open" class="v-button v-button--primary">
          <span class="v-icon fa-regular fa-folder-tree"></span> Kategorie wählen &amp; aufnehmen
        </button>
      </div>`;
    gateBody.querySelector<HTMLButtonElement>('#go-newcat-open')?.addEventListener('click', openCategoryGate);
    openCategoryGate();

    // Kategorie am Vario-Listing setzen (categoryRefs) und danach die Lister-Zeile anlegen.
    async function registerWithCategory(gambioCategoryIds: number[], mainGambioId: number | null): Promise<void> {
      setStatusBar(ctx.contentEl, 'loading', 'Kategorie setzen & aufnehmen…');
      let varioCatId: string | null = null;
      let varioCatName: string | null = null;
      if (gambioCategoryIds.length > 0) {
        try {
          const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-assign-listing-category`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ articleId, listingId, shopKey: storefront, gambioCategoryIds, mainGambioCategoryId: mainGambioId ?? undefined }),
          });
          const out = (await res.json().catch(() => ({}))) as {
            ok?: boolean;
            mainVarioCategoryId?: string | null;
            mainVarioCategoryName?: string | null;
            error?: { code?: string; message?: string };
          };
          if (res.status === 409 && out.error?.code === 'CATEGORY_NOT_IN_VARIO') {
            setStatusBar(ctx.contentEl, 'error', 'Kategorie(n) noch nicht im Vario-Baum - bitte erst den Kategorien-Sync starten.');
            return;
          }
          if (!res.ok || out.error) throw new Error(out.error?.message ?? `HTTP ${res.status}`);
          varioCatId = out.mainVarioCategoryId ?? null;
          varioCatName = out.mainVarioCategoryName ?? null;
        } catch (e) {
          setStatusBar(ctx.contentEl, 'error', `Kategorie setzen fehlgeschlagen: ${(e as Error).message}`);
          return;
        }
      }
      if (await createStandaloneListing(varioCatId, varioCatName)) {
        setStatusBar(ctx.contentEl, 'success', 'In den Gambio-Lister aufgenommen.');
        await refresh();
      }
    }

    // Modal mit dem Baum-Picker (Mehrfachauswahl + Hauptkategorie). Leerer Baum → Fallback,
    // den Artikel ohne Kategorie aufzunehmen (später via Picker nachziehbar).
    function openCategoryGate(): void {
      const modal = openModal({
        title: 'Gambio-Kategorie wählen',
        icon: 'fa-folder-tree',
        width: 640,
        body: '<div id="go-newcat-tree"><div class="v-text-muted">Kategorien laden…</div></div>',
        footer: `
          <button type="button" class="v-button v-button--ghost" data-modal-cancel>Abbrechen</button>
          <button type="button" class="v-button v-button--save" id="go-newcat-save" disabled>
            <span class="v-icon fa-regular fa-check"></span> Zuweisen &amp; aufnehmen
          </button>`,
        onMount: (rootEl) => {
          rootEl.style.alignItems = 'flex-start';
          const treeHost = rootEl.querySelector<HTMLElement>('#go-newcat-tree')!;
          const saveBtn = rootEl.querySelector<HTMLButtonElement>('#go-newcat-save')!;
          let draft: { selected: number[]; mainId: number | null } = { selected: [], mainId: null };
          void (async () => {
            interface TreeResp { ok?: boolean; tree?: CategoryTreeNode[]; error?: { message?: string } }
            let data: TreeResp;
            try {
              const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-category-tree`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ shopKey: storefront, articleId, listingId }),
              });
              data = (await res.json().catch(() => ({}))) as TreeResp;
              if (!res.ok || !data.ok) throw new Error(data.error?.message ?? `HTTP ${res.status}`);
            } catch (e) {
              treeHost.innerHTML = `<div class="v-text-muted">Kategorien nicht ladbar. ${escapeHtml(describeGambioError((e as Error).message).text)}</div>`;
              return;
            }
            const tree = data.tree ?? [];
            if (tree.length === 0) {
              treeHost.innerHTML = `<div class="v-banner v-banner-info">
                <span class="v-icon fa-light fa-sitemap"></span>
                <div>Noch keine Kategorien im Gambio-Shop synchronisiert - unter Einstellungen den Kategorien-Sync starten. Du kannst den Artikel vorerst ohne Kategorie aufnehmen und später zuweisen.</div>
              </div>`;
              saveBtn.disabled = false;
              saveBtn.innerHTML = '<span class="v-icon fa-regular fa-inbox"></span> Ohne Kategorie aufnehmen';
              saveBtn.addEventListener('click', () => { modal.close(); void registerWithCategory([], null); });
              return;
            }
            renderCategoryTreePicker(treeHost, {
              tree,
              selected: draft.selected,
              mainId: draft.mainId,
              onChange: (state) => { draft = state; saveBtn.disabled = state.selected.length === 0; },
            });
            saveBtn.addEventListener('click', () => { modal.close(); void registerWithCategory(draft.selected, draft.mainId); });
          })();
        },
      });
    }
  }

  // ── Bereits im Lister → Detail-Pane inline ───────────────────────────────
  async function renderInLister(row: InboxRow): Promise<void> {
    const body = ctx.contentEl.querySelector<HTMLElement>('#slot-body')!;
    const catDisplay = row.vario_category_name
      ? escapeHtml(row.vario_category_name)
      : row.vario_category_id
        ? escapeHtml(row.vario_category_id)
        : '— keine Kategorie zugewiesen —';
    // Variantenkinder erben die Kategorie vom Variantenvater - der Hinweis steht bereits im
    // Detail-Pane-Header (renderChildDetail), hier daher KEIN zusätzliches (doppeltes) Banner.
    const noCatBanner = row.is_variant_child
      ? ''
      : (!row.vario_category_id && !row.gambio_category_id)
        ? `<div class="v-banner v-banner-info" style="margin-bottom:8px;">
             <span class="v-icon fa-light fa-circle-info"></span>
             <div>Noch keine Kategorie zugewiesen - unten Kategorien auswählen.</div>
           </div>`
        : '';
    const uploadBtn = `<button type="button" id="go-upload-to-gambio" class="v-button v-button--save">
        <span class="v-icon fa-regular fa-cloud-arrow-up"></span> Sofort zu Gambio übertragen
      </button>`;
    // ── Echten Gambio-Status ermitteln ─────────────────────────────────────────────────────────
    // Wichtig: gambio.listings.status kennt KEIN „übertragen/gelistet" (bleibt nach Push auf
    // 'mapped'). Die Wahrheit steht woanders:
    //   • „übertragen" = Mapping mit marketplace_unit_id vorhanden (das Produkt existiert in Gambio),
    //   • „aktiv"      = Live-isActive aus Gambio (Edge Fn gambio-product-active, GET).
    // Basis-URL kommt aus dem Shop (gambio_list_shops), NICHT dem Host dieser App.
    const shopsData = (await ctx.supabase.rpc('gambio_list_shops')).data;
    const shop0 = ((Array.isArray(shopsData) ? shopsData : []) as Array<{ shop_key?: string; base_url?: string | null }>)[0];
    const shopKey = shop0?.shop_key ?? 'default';
    const baseUrl = shop0?.base_url ?? null;
    // ALLE Mappings lesen, nicht limit(1): ein Artikel kann in Gambio zugleich Varianten-Kind
    // (`<pid>-v<vid>`) UND eigenes flaches Produkt (`<pid>`) sein. Das ist eine Fehlkonfiguration
    // (derselbe Artikel liegt doppelt im Shop). Regel wie im Worker: die VARIANTE gewinnt — der
    // Shop-/Admin-Link zeigt dann auf das Variantenprodukt, nicht auf die Dublette. Mit limit(1) und
    // ohne order by entschied das vorher der Zufall.
    let unitId: string | null = null;
    let duplicateFlatId: string | null = null;
    try {
      const { data: mps } = await ctx.supabase.schema('vario').from('article_mappings')
        .select('marketplace_unit_id')
        .eq('marketplace', 'gambio').eq('vario_article_id', articleId);
      const ids = ((mps ?? []) as Array<{ marketplace_unit_id?: string | null }>)
        .map((r) => r.marketplace_unit_id)
        .filter((v): v is string => !!v);
      const childId = ids.find((v) => v.indexOf('-v') > 0) ?? null;
      const flatId = ids.find((v) => v.indexOf('-v') <= 0) ?? null;
      unitId = childId ?? flatId;
      if (childId && flatId) duplicateFlatId = flatId;
    } catch { /* tolerant */ }

    // Variantenvater: der hat KEINE eigene Mapping-Zeile — nur seine Kinder haben eine. Ohne den
    // Griff zum Familien-Anker stand am Vater deshalb „Noch nicht in den Gambio-Shop übertragen",
    // obwohl das Produkt AUS dem Shop kam (Betreiber, 27.08.2026, Artikel 22025). Die
    // `gambio.variant_groups`-Zeile ist der Beleg: sie entsteht, wenn die Familie gebaut ist —
    // beim Import aus dem Shop ebenso wie bei der Anlage aus VARIO, in beiden Fällen existiert das
    // Produkt danach im Shop. Ihre `gambio_product_id` ist zugleich die Produkt-ID für den
    // Shop-Deeplink, der am Vater sonst auch fehlte.
    if (!unitId) {
      try {
        const { data: fam } = await ctx.supabase
          .schema('gambio').from('variant_groups')
          .select('gambio_product_id')
          .eq('vario_main_article_id', articleId)
          .limit(1);
        const famId = ((fam ?? []) as Array<{ gambio_product_id?: string | null }>)[0]?.gambio_product_id ?? null;
        if (famId) unitId = famId;
      } catch { /* tolerant — dann bleibt es beim bisherigen Verhalten */ }
    }
    const transferred = !!unitId;
    // Variantenkinder → Elternprodukt (gambioProductId strippt "-v<id>"). Im Vario-iframe (kein
    // allow-popups) MUSS der Shop-Link ein <a target="_blank"> sein, kein window.open.
    const shopUrl = (transferred && baseUrl) ? gambioProductFrontendUrl(baseUrl, gambioProductId(unitId!)) : null;
    // Backend-Deeplink (Produkt-Editor) — funktioniert unabhängig vom Aktiv-Status.
    const adminUrl = (transferred && baseUrl) ? gambioProductAdminUrl(baseUrl, gambioProductId(unitId!)) : null;

    // Fehlkonfiguration sichtbar machen: der Artikel liegt zweimal im Shop (als Variante und als
    // eigenes Produkt). Der Worker stoppt in dem Fall den Flach-Export und pflegt nur die Variante;
    // ohne diesen Hinweis würde der Betreiber die Dublette nie bemerken. Deeplink auf die Dublette,
    // damit sie direkt geprüft werden kann. Wir schlagen hier bewusst KEINE Löschung vor.
    const duplicateAdminUrl = (duplicateFlatId && baseUrl)
      ? gambioProductAdminUrl(baseUrl, gambioProductId(duplicateFlatId))
      : null;
    const dupBanner = duplicateFlatId
      ? `<div class="v-banner v-banner-warning" style="margin-bottom:8px;">
           <span class="v-icon fa-light fa-triangle-exclamation"></span>
           <div>Dieser Artikel liegt <strong>zweimal</strong> im Gambio-Shop: als Variante
             (${escapeHtml(unitId ?? '')}) und als eigenes Produkt
             ${duplicateAdminUrl
               ? `<a href="${duplicateAdminUrl}" target="_blank" rel="noopener">Nr. ${escapeHtml(duplicateFlatId)}</a>`
               : `Nr. ${escapeHtml(duplicateFlatId)}`}.
             Gepflegt wird die Variante; das eigene Produkt wird nicht mehr aktualisiert.
             Bitte prüfen, welches der beiden bestehen bleiben soll.</div>
         </div>`
      : '';

    // Live-Aktiv-Status: nur wenn übertragen UND kein Variantenkind (Gambio-Varianten tragen kein
    // isActive). true/false = live gelesen; null = übertragen, aber Status nicht abrufbar.
    let activeState: boolean | null = null;
    // Aktiv-Status des Link-ZIELS: der Shop-Link zeigt bei Varianten aufs Elternprodukt. Für Kinder
    // (kein eigenes isActive) wird der Aktiv-Status des Elternprodukts gelesen, damit der Link nicht
    // ins Leere führt, wenn der Vater inaktiv ist. Für Nicht-Kinder = der eigene Status (siehe unten).
    let parentActive: boolean | null = null;
    if (transferred && !row.is_variant_child) {
      try {
        const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-product-active`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
          // `productId` mitgeben, wenn wir ihn kennen: ein VARIANTENVATER hat keine
          // `article_mappings`-Zeile, `gambio-product-active` kann ihn ueber `articleId` also nicht
          // aufloesen und antwortete „nicht abrufbar" (Betreiber, 27.08.2026: „Uebertragen
          // (Aktiv-Status derzeit nicht abrufbar)"). `unitId` kommt beim Vater aus dem
          // Familien-Anker; `productId` gewinnt in der Function.
          body: JSON.stringify({
            articleId,
            ...(unitId ? { productId: gambioProductId(unitId) } : {}),
            action: 'get',
            shopKey,
          }),
        });
        const out = (await res.json().catch(() => ({}))) as { ok?: boolean; isActive?: boolean };
        if (res.ok && out.ok) activeState = out.isActive === true;
      } catch { /* activeState bleibt null (Status nicht abrufbar) */ }
    } else if (transferred && row.is_variant_child && unitId) {
      // Variantenkind: Aktiv-Status des ELTERNprodukts (productId gewinnt in gambio-product-active).
      try {
        const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-product-active`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ productId: gambioProductId(unitId), action: 'get', shopKey }),
        });
        const out = (await res.json().catch(() => ({}))) as { ok?: boolean; isActive?: boolean };
        if (res.ok && out.ok) parentActive = out.isActive === true;
      } catch { /* parentActive bleibt null → Link bleibt klickbar */ }
    }
    // Kategorie-Zeile im Statusbanner. Für einen ÜBERTRAGENEN Artikel ist die Wahrheit der SHOP,
    // nicht der Vario-Spiegel `vario_category_name`: der Spiegel kann nur überschrieben, nie geleert
    // werden (`gambio_set_listing_category` nutzt `coalesce`) und zeigte nach einem Entfernen der
    // Kategorie im Vario-Verkaufskanal weiter die alte Kategorie (User-Befund 2026-08-21). Deshalb
    // wird der Live-Stand nachgeladen (s. loadShopCategoryLine) und diese Zeile ersetzt.
    const varioCatLine = `Ziel-Kategorie aus VARIO (wird bei Anlage/Import verwendet): <strong>${catDisplay}</strong>`;
    let catLine = varioCatLine;
    // Frontend-Link: nur klickbar, wenn das Link-ZIEL NICHT nachweislich inaktiv ist — das Shop-Frontend
    // zeigt inaktive Artikel nicht, der Link liefe ins Leere. Bei unbekanntem Status (null) bleibt er aktiv.
    // Als Funktion, damit paintState() ihn nach einem Aktiv-Umschalten neu zeichnen kann.
    function shopLinkHtml(): string {
      if (!shopUrl) return '';
      // Kind → Aktiv-Status des Elternprodukts; sonst der eigene (live via Toggle aktualisierte) Status.
      const targetActive = row.is_variant_child ? parentActive : activeState;
      if (targetActive === false) {
        return `<span class="v-button" aria-disabled="true" title="Artikel ist im Gambio-Shop inaktiv und daher im Frontend nicht aufrufbar" style="opacity:.5;cursor:not-allowed;pointer-events:none;">
               <span class="v-icon fa-regular fa-arrow-up-right-from-square"></span> Im Gambio-Shop öffnen
             </span>`;
      }
      return `<a href="${escapeHtml(shopUrl)}" target="_blank" rel="noopener" class="v-button" title="Artikel im Gambio-Shop ansehen">
               <span class="v-icon fa-regular fa-arrow-up-right-from-square"></span> Im Gambio-Shop öffnen
             </a>`;
    }
    // Backend-Link: immer verfügbar (auch bei inaktivem Artikel), öffnet den Produkt-Editor im Gambio-Admin.
    const adminLinkBtn = adminUrl
      ? `<a href="${escapeHtml(adminUrl)}" target="_blank" rel="noopener" class="v-button v-button--ghost" title="Artikel im Gambio-Backend (Produkt-Editor) öffnen">
           <span class="v-icon fa-regular fa-pen-to-square"></span> Im Gambio-Backend öffnen
         </a>`
      : '';
    body.innerHTML = `
      <!-- Kopfzeile: Aktiv-Schalter links, „Gambio verbunden" + (Shop-Link, wenn angelegt) +
           Übertragen rechts. Der Kategorien-Block lebt im Übersicht-Tab des Detail-Pane. -->
      <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:10px;">
        <div id="go-active-toggle"></div>
        <div style="margin-left:auto;display:flex;gap:10px;align-items:center;flex-wrap:wrap;">
          ${connectionPillHtml()}
          <span id="go-shop-link">${shopLinkHtml()}</span>
          ${adminLinkBtn}
          ${uploadBtn}
        </div>
      </div>
      ${dupBanner}
      ${noCatBanner}
      <div id="go-gambio-state" class="v-banner" style="margin-bottom:10px;"></div>
      <div id="slot-pane" class="kl-detail-host"></div>`;

    // ── Zustands-Banner + Aktiv-Schalter aus dem ECHTEN Status zeichnen (nicht aus listings.status) ──
    function stateInfo(): { banner: string; icon: string; text: string } {
      if (row.status === 'archived') return { banner: 'v-banner-warn', icon: 'fa-box-archive', text: 'Archiviert' };
      if (!transferred) return { banner: 'v-banner-info', icon: 'fa-circle-info', text: 'Noch nicht in den Gambio-Shop übertragen' };
      if (row.is_variant_child) return { banner: 'v-banner-ok', icon: 'fa-circle-check', text: 'Variante eines übertragenen Artikels' };
      if (activeState === true) return { banner: 'v-banner-ok', icon: 'fa-circle-check', text: 'Übertragen und im Gambio-Shop <strong>aktiv</strong>' };
      if (activeState === false) return { banner: 'v-banner-warn', icon: 'fa-eye-slash', text: 'Übertragen, im Gambio-Shop <strong>nicht aktiv</strong>' };
      return { banner: 'v-banner-info', icon: 'fa-circle-info', text: 'Übertragen (Aktiv-Status derzeit nicht abrufbar)' };
    }
    function paintState(): void {
      const stEl = body.querySelector<HTMLElement>('#go-gambio-state');
      if (stEl) {
        const si = stateInfo();
        stEl.className = `v-banner ${si.banner}`;
        stEl.innerHTML = `<span class="v-icon fa-light ${si.icon}"></span><div>${si.text} · ${catLine}</div>`;
      }
      // Shop-Frontend-Link an den aktuellen Aktiv-Status anpassen (inaktiv → deaktiviert).
      const shopEl = body.querySelector<HTMLElement>('#go-shop-link');
      if (shopEl) shopEl.innerHTML = shopLinkHtml();
      const toggleHost = body.querySelector<HTMLElement>('#go-active-toggle');
      if (!toggleHost) return;
      // Aktiv-Schalter nur, wenn übertragen, kein Variantenkind und der Live-Status abrufbar war.
      if (!transferred || row.is_variant_child || activeState === null) { toggleHost.innerHTML = ''; return; }
      const on = activeState === true;
      toggleHost.innerHTML = `
        <button type="button" id="go-active-switch" class="go-sw${on ? ' on' : ''}">
          <span class="track"></span>
          <span>${on ? 'Im Gambio-Shop aktiv' : 'Im Gambio-Shop nicht aktiv'}</span>
        </button>`;
      toggleHost.querySelector<HTMLButtonElement>('#go-active-switch')?.addEventListener('click', () => {
        const next = !on;
        toggleHost.querySelector<HTMLButtonElement>('#go-active-switch')?.setAttribute('disabled', '');
        setStatusBar(ctx.contentEl, 'loading', 'Aktiv-Status setzen…');
        void (async () => {
          try {
            const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-product-active`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ articleId, action: 'set', isActive: next, shopKey }),
            });
            const out = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: { message?: string } };
            if (!res.ok || !out.ok) throw new Error(out.error?.message ?? `HTTP ${res.status}`);
            activeState = next;
            setStatusBar(ctx.contentEl, 'success', next ? 'Produkt im Shop aktiv.' : 'Produkt im Shop inaktiv.');
          } catch (e) {
            setStatusBar(ctx.contentEl, 'error', `Aktiv-Status setzen fehlgeschlagen: ${(e as Error).message}`);
          } finally {
            paintState();
          }
        })();
      });
    }
    paintState();

    // ── Kategorie-Zeile aus dem SHOP nachladen ────────────────────────────────────────────────
    // Nur für übertragene Artikel (ohne Mapping gibt es im Shop nichts zu lesen). Für Varianten-
    // kinder liest die Edge Fn die Kategorien des Elternprodukts — nur dieses liegt in Kategorien.
    // Jeder Fehlerfall fällt sichtbar auf die Vario-Ziel-Kategorie zurück, statt still zu lügen.
    async function loadShopCategoryLine(): Promise<void> {
      if (!transferred) return;
      const res = await fetchShopCategories(
        { supabaseUrl: ctx.supabaseUrl, token: ctx.session.token }, articleId, shopKey,
      );
      const prefix = row.is_variant_child ? 'Kategorien im Gambio-Shop (am Variantenprodukt)' : 'Kategorien im Gambio-Shop';
      if (res.kind === 'ok') {
        catLine = res.categories.length === 0
          ? `${prefix}: <strong>keiner Kategorie zugeordnet</strong>`
          : `${prefix}: ${res.categories
              .map((c) => (c.isMain
                ? `<strong>${escapeHtml(c.name)}</strong> <span class="v-text-muted">(Hauptkategorie)</span>`
                : escapeHtml(c.name)))
              .join(', ')}`;
      } else if (res.kind === 'gone') {
        catLine = 'Produkt im Gambio-Shop nicht mehr vorhanden — „Sofort zu Gambio übertragen" legt es neu an.';
      } else if (res.kind === 'error') {
        catLine = `${varioCatLine} <span class="v-text-muted">(Shop-Kategorien derzeit nicht abrufbar)</span>`;
      }
      // 'not-listed': Mapping-Rennen (gerade übertragen, Mapping noch nicht da) → Vario-Zeile bleibt.
      paintState();
    }
    void loadShopCategoryLine();

    // Live-Polling nach dem Push: der Worker legt das Produkt asynchron an. Wir pollen das
    // Mapping (marketplace_unit_id) und laden den Slot neu, sobald die Übertragung fertig ist →
    // Banner wechselt automatisch von „Übertragung läuft…" zum echten übertragen/aktiv-Zustand.
    let pollTimer: number | null = null;
    const stopPoll = (): void => { if (pollTimer !== null) { clearTimeout(pollTimer); pollTimer = null; } };
    const startPostPushPolling = (): void => {
      stopPoll();
      const wasTransferred = transferred;
      let attempts = 0;
      const tick = async (): Promise<void> => {
        attempts++;
        try {
          const { data: mp } = await ctx.supabase.schema('vario').from('article_mappings')
            .select('marketplace_unit_id')
            .eq('marketplace', 'gambio').eq('vario_article_id', articleId)
            .limit(1).maybeSingle();
          const nowUnit = (mp as { marketplace_unit_id?: string | null } | null)?.marketplace_unit_id ?? null;
          // Erstübertragung: fertig, sobald das Mapping existiert. Update (schon übertragen): nach
          // ein paar Ticks einmal neu einlesen (Live-isActive frisch), dann stoppen.
          if ((!wasTransferred && nowUnit) || (wasTransferred && attempts >= 2)) { stopPoll(); await refresh(); return; }
        } catch { /* weiter versuchen */ }
        if (attempts >= 24) { stopPoll(); return; } // ~2 min Timeout, dann aufgeben
        pollTimer = window.setTimeout(() => void tick(), 5000);
      };
      pollTimer = window.setTimeout(() => void tick(), 5000);
    };

    /**
     * Wartet die Jobs der Übertragung ab und meldet das Ergebnis. Läuft im Hintergrund, blockiert
     * also weder den Knopf noch das Status-Polling. Läuft ein Job in das awaitJob-Timeout, bleibt
     * es beim ruhigen Hinweis auf das Protokoll — die Übertragung läuft dann ja noch.
     */
    async function reportPushOutcome(jobIds: ReadonlyArray<string>): Promise<void> {
      if (jobIds.length === 0) return;
      const outcomes: BulkJobOutcome[] = [];
      try {
        for (const id of jobIds) {
          const job = await awaitJob(ctx.supabase, id, 120_000);
          outcomes.push({ status: job.status, error: job.error, result: job.result, ...(jobStepLabel(job.type) !== undefined ? { label: jobStepLabel(job.type)! } : {}) });
        }
      } catch {
        setStatusBar(ctx.contentEl, '', 'Übertragung läuft noch — das Ergebnis steht im Protokoll.');
        return;
      }
      const summary = summarizeBulkOutcome(outcomes, {
        actionLabel: 'Übertragung zu Gambio',
        successMessage: 'Übertragung zu Gambio abgeschlossen.',
      });
      if (!summary.hasFailures) {
        setStatusBar(ctx.contentEl, 'success', summary.message);
        return;
      }
      // Fehler ins Modal: der Einzeiler mitten im Slot war unübersichtlich (User-Befund
      // 2026-08-21). Im Modal steht jeder Fehler auf seiner Zeile, ungekürzt, mit „Als JSON
      // kopieren". Die Statusleiste behält die Kurzfassung, damit nach dem Schließen sichtbar
      // bleibt, dass etwas schiefging.
      setStatusBar(ctx.contentEl, 'error', `Übertragung mit ${summary.failedCount} Fehler(n) beendet.`);
      openErrorModal({
        title: 'Übertragung teilweise fehlgeschlagen',
        intro: 'Die Ursache steht auch am jeweiligen Artikel unter „Letzter Fehler".',
        items: summary.items,
        context: { action: 'gambio_publish_or_update', articleId, jobIds: [...jobIds] },
        json: { jobIds: [...jobIds], outcomes },
      });
    }

    const upBtn = body.querySelector<HTMLButtonElement>('#go-upload-to-gambio');
    upBtn?.addEventListener('click', () => {
      upBtn.setAttribute('disabled', '');
      void (async () => {
        try {
          if (!shopKey) throw new Error('Kein Gambio-Shop verbunden.');
          const { data: enqueued, error } = await ctx.supabase.rpc('gambio_enqueue_publish_or_update', {
            _shop_key: shopKey,
            _vario_article_ids: [articleId],
          });
          if (error) throw new Error(error.message);
          setStatusBar(ctx.contentEl, 'success', 'Übertragung zu Gambio gestartet.');
          // Die RPC gibt die Job-IDs zurück (setof uuid). Bis 2026-08-21 wurden sie verworfen: die
          // Übertragung galt als „gestartet" und das Ergebnis erfuhr der Anwender NIE — ein Artikel,
          // der am 401 des Shops scheiterte, sah genauso aus wie ein erfolgreicher (User-Befund).
          // Jetzt warten wir die Jobs im Hintergrund ab und melden das Ergebnis in der Statusleiste.
          const pushJobIds = (Array.isArray(enqueued) ? enqueued : [enqueued])
            .filter((x): x is string => typeof x === 'string');
          void reportPushOutcome(pushJobIds);
          // „Übertragung läuft…" anzeigen; das Polling aktualisiert den echten Zustand automatisch,
          // sobald der Worker das Produkt angelegt/aktualisiert hat.
          const stEl = body.querySelector<HTMLElement>('#go-gambio-state');
          if (stEl) {
            stEl.className = 'v-banner v-banner-info';
            stEl.innerHTML = `<span class="v-icon fa-light fa-spinner-third fa-spin"></span><div>Übertragung läuft… <span class="v-text-muted">Der Status aktualisiert sich automatisch.</span> · ${catLine}</div>`;
          }
          startPostPushPolling();
        } catch (e) {
          setStatusBar(ctx.contentEl, 'error', `Übertragung fehlgeschlagen: ${(e as Error).message}`);
        } finally {
          upBtn.removeAttribute('disabled');
        }
      })();
    });
    const paneEl = body.querySelector<HTMLDivElement>('#slot-pane')!;
    await renderListingDetailPane(paneEl, {
      supabase: ctx.supabase,
      session: { token: ctx.session.token, tenantId: ctx.session.tenantId },
      supabaseUrl: ctx.supabaseUrl,
    }, row.id, {
      kind: 'slot',
      onListingChanged: () => void refresh(),
      onDeleted: () => void refresh(),
    });

    // Darstellung + Google-Shopping nur für übertragene Produkte (Live-GETs; würden sonst 404
    // liefern). Der Aktiv-Schalter wird bereits in paintState() gezeichnet. go-display-settings/
    // -google-shopping leben IM Übersicht-Tab des Detail-Pane (erst nach dem obigen await da).
    if (transferred && !row.is_variant_child) {
      const displayHost = paneEl.querySelector<HTMLElement>('#go-display-settings');
      if (displayHost) void loadDisplaySettings(displayHost);
      const gsHost = paneEl.querySelector<HTMLElement>('#go-google-shopping');
      if (gsHost) void loadGoogleShopping(gsHost);
    }
    // Kategorie-Picker: für ALLE Nicht-Kinder-Zeilen, auch VOR der ersten Übertragung (pending/
    // mapping/error/archived) - die Zuweisung soll schon vor dem ersten Push möglich sein.
    // Variantenkinder erben die Kategorie vom Vater → kein eigener Picker.
    if (!row.is_variant_child) {
      // Kategorien-Block lebt jetzt im Übersicht-Tab des Detail-Pane (Platzhalter #go-shop-categories
      // in renderListingDetailPane) - daher aus paneEl statt body auflösen.
      const catsHost = paneEl.querySelector<HTMLElement>('#go-shop-categories');
      if (catsHost) void loadCategoryPicker(catsHost, row);
    }
  }

  // ── Fehler-Banner mit "Erneut versuchen" ─────────────────────────────────
  function renderErrorBanner(msg: string): void {
    setStatusBar(ctx.contentEl, 'error', 'Fehler');
    const body = ctx.contentEl.querySelector<HTMLElement>('#slot-body')!;
    body.innerHTML = `
      <div class="v-banner v-banner-danger" style="margin-bottom:8px;">
        <span class="v-icon fa-light fa-circle-xmark"></span>
        <div>
          <div>${escapeHtml(msg)}</div>
          <button type="button" id="retry-btn" class="v-button v-button--ghost" style="margin-top:6px;">
            <span class="v-icon fa-regular fa-rotate-right"></span> Erneut versuchen
          </button>
        </div>
      </div>`;
    body.querySelector<HTMLButtonElement>('#retry-btn')!.addEventListener('click', () => void refresh());
  }

  // Fataler Fehler ohne Retry (z. B. kein Shop konfiguriert).
  function renderNoBannerError(msg: string): void {
    setStatusBar(ctx.contentEl, 'error', 'Fehler');
    const body = ctx.contentEl.querySelector<HTMLElement>('#slot-body')!;
    body.innerHTML = `
      <div class="v-banner v-banner-danger" style="margin-bottom:8px;">
        <span class="v-icon fa-light fa-triangle-exclamation"></span>
        <div>${escapeHtml(msg)}</div>
      </div>`;
  }
}

// ── Markup + Utils ─────────────────────────────────────────────────────────

function renderShell(): string {
  return `
    <style>
      /* Header/Logo self-contained scopen: die kanonischen Regeln liegen scoped unter
         .go-orders/.go-products (gambio-orders.css) - der Inline-Slot lädt die nicht,
         sonst rendert das Gambio-Logo in Naturgröße und überdeckt alles. Werte 1:1 aus
         gambio-orders.css. */
      .go-lister .apphead { display:flex; align-items:center; gap:14px; }
      .go-lister .brand { display:inline-flex; align-items:center; text-decoration:none; flex:none; }
      .go-lister .brand img { height:20px; display:block; }
      .go-lister .gambio-logo { height:30px; width:auto; flex:none; mix-blend-mode:multiply; }
      .go-lister .hdivider { width:1px; height:24px; background:var(--border); flex:none; }
      .go-lister .heyebrow { font-size:10.5px; font-weight:600; letter-spacing:.11em; text-transform:uppercase; color:var(--muted); }
      .go-lister .htitle h1 { font-size:20px; font-weight:600; letter-spacing:-0.01em; margin:0; line-height:1.2; }
      .go-lister .hactions { margin-left:auto; display:flex; gap:13px; align-items:center; }
      /* kl-detail-host (kf-page.css) ist fuer Vollflaechen-Slots gedacht: flex:1 + overflow:auto
         → in unserem grow-to-fit-Inline-Slot (kein fixes Hoehen-Layout) kollabiert flex:1 und
         overflow:auto schneidet die Feldliste auf wenige Zeilen ab. Hier neutralisieren: der
         Inhalt fliesst natuerlich, das iframe waechst auf volle Hoehe (wie Otto-Verkaufskanal). */
      .go-lister .kl-detail-host { flex:none; min-height:0; height:auto; overflow:visible; }
      /* DER eigentliche Hebel (Spiegel von otto-autogrow): bridge.css zwingt html/body/main#app
         auf height:100% + overflow:hidden → body.scrollHeight bleibt = Viewport, das Auto-Höhen-
         iframe wächst nie. Hochspezifischer Override bricht die Kette auf → scrollHeight = Inhalt. */
      html.go-lister-autogrow, html.go-lister-autogrow body, html.go-lister-autogrow main#app { height:auto; overflow:visible; }
      html.go-lister-autogrow main#app > section#content { flex:0 0 auto; }
      /* isActive-Switch (Shop-Sichtbarkeit) - Optik wie der Grid-Switch (.sw-toggle), hier
         .go-lister-scoped, weil der Inline-Slot gambio-products.css nicht laedt. */
      .go-lister .go-sw { display:inline-flex; align-items:center; gap:8px; background:#fff; border:1px solid #E6E6E9; border-radius:9px; padding:6px 12px; cursor:pointer; font:inherit; font-size:12.5px; color:#8a8f96; }
      .go-lister .go-sw .track { width:28px; height:16px; border-radius:8px; background:#b6bcc4; position:relative; transition:background .15s; flex:none; }
      .go-lister .go-sw .track::after { content:''; position:absolute; top:2px; left:2px; width:12px; height:12px; border-radius:50%; background:#fff; transition:left .15s; }
      .go-lister .go-sw.on { color:#6a7a4a; }
      .go-lister .go-sw.on .track { background:var(--vario-green, #7AB51D); }
      .go-lister .go-sw.on .track::after { left:14px; }
      .go-lister .go-sw[disabled] { opacity:.55; cursor:default; }
      /* Darstellungs-Panel (settings-Felder) - Design-System-Optik, .go-lister-scoped. */
      .go-lister .go-ds { border:1px solid #E6E6E9; border-radius:9px; background:#fff; padding:8px 12px; }
      .go-lister .go-ds > summary { cursor:pointer; font-weight:600; font-size:13px; color:#3a3f46; list-style:none; display:flex; align-items:center; justify-content:space-between; gap:12px; }
      .go-lister .go-ds > summary::-webkit-details-marker { display:none; }
      .go-lister .go-ds > summary .go-ds-sum-title { display:inline-flex; align-items:center; gap:6px; min-width:0; }
      /* Save-Button in der Summary: kompakter, damit die Kopfzeile schlank bleibt. */
      .go-lister .go-ds > summary .go-ds-savebtn { flex:none; height:30px; padding:0 12px; font-weight:500; cursor:pointer; }
      .go-lister .go-ds-body { display:flex; flex-direction:column; gap:10px; margin-top:10px; }
      .go-lister .go-ds-toggles { display:flex; flex-wrap:wrap; gap:6px; }
      .go-lister .go-ds-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(220px,1fr)); gap:8px 14px; }
      .go-lister .go-ds-row { display:flex; flex-direction:column; gap:3px; font-size:12px; color:#6a7078; }
      .go-lister .go-ds-row select, .go-lister .go-ds-row input { font:inherit; font-size:12.5px; padding:5px 8px; border:1px solid #E6E6E9; border-radius:7px; background:#fff; color:#3a3f46; }
    </style>
    <div class="kf-page go-lister" style="padding:10px;">
      <header class="apphead" style="margin-bottom:8px;">
        <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">
          <img src="/logo-black.png" alt="go eCommerce" />
        </a>
        <img class="gambio-logo" src="/gambio-logo.png?v=2" alt="Gambio" />
        <span class="hdivider"></span>
        <div class="htitle"><div class="heyebrow">Gambio · Lister</div><h1 id="slot-subtitle">Artikel Lister</h1></div>
        <div class="hactions">${helpButtonHtml('artikel-detail')}<span id="slot-status"></span></div>
      </header>
      <div id="slot-body"></div>
    </div>`;
}

function readSlotParams(): { articleId: string | null; listingId: string | null } {
  const q = new URL(window.location.href).searchParams;
  return {
    articleId: q.get('articleId') ?? q.get('article_id') ?? q.get('id'),
    listingId: q.get('listingId') ?? q.get('listing_id'),
  };
}

// Auto-Ausblenden für Erfolgsmeldungen: eine 'success'-Meldung ist eine transiente Bestätigung
// (z. B. „Übertragung gestartet", „Kategorien gespeichert") und wird nach kurzer Zeit selbst geleert.
// 'loading' wird ohnehin von der Folgemeldung ersetzt; 'error' bleibt stehen (muss lesbar bleiben).
let statusClearTimer: number | undefined;
const STATUS_SUCCESS_TTL_MS = 4000;
function setStatusBar(host: HTMLElement, kind: 'loading' | 'success' | 'error' | '', msg: string): void {
  const el = host.querySelector<HTMLElement>('#slot-status');
  if (!el) return;
  // Vorherigen Auto-Clear abbrechen - jede neue Meldung übernimmt (bzw. verwaltet ihr eigenes TTL).
  if (statusClearTimer !== undefined) { window.clearTimeout(statusClearTimer); statusClearTimer = undefined; }
  if (!kind) { el.innerHTML = ''; return; }
  const icon = kind === 'loading' ? 'fa-spinner-third fa-spin' : kind === 'success' ? 'fa-circle-check' : 'fa-circle-xmark';
  const cls = kind === 'success' ? 'v-text-success' : kind === 'error' ? 'v-text-error' : 'v-text-muted';
  el.innerHTML = `<span class="${cls}" style="font-size:0.88em;"><span class="v-icon fa-regular ${icon}"></span> ${escapeHtml(msg)}</span>`;
  if (kind === 'success') {
    statusClearTimer = window.setTimeout(() => {
      statusClearTimer = undefined;
      const cur = host.querySelector<HTMLElement>('#slot-status');
      if (cur) cur.innerHTML = '';
    }, STATUS_SUCCESS_TTL_MS);
  }
}

function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
